import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { dateUtil } from "src/app/support/date-util";
import { pickBy, round } from "lodash-es";
import {
  catchError,
  defer,
  forkJoin,
  from,
  iif,
  lastValueFrom,
  Observable,
  of,
  switchMap
} from "rxjs";
import { LetterheadBackend } from "src/app/backend/api/letterhead/letterhead.backend";
import { PaymentMethodBackend } from "src/app/backend/api/payment-method.backend";
import { StorageBackend } from "src/app/backend/api/storage/storage.backend";
import {
  CashClosure,
  CashClosurePrintViewModel
} from "src/app/backend/models/invoice/cash-closure";
import { RegionService } from "src/app/services/feature-access-control/region.service";
import { PrintService } from "src/app/services/print/print.service";
import { TemplateService } from "src/app/services/template.service";
import { blobToDataUrl } from "src/app/support/blob";
import { formatCurrency } from "src/app/support/currency";
import { getCustomerNominative } from "src/app/support/customer";
import { UserNotify } from "src/app/support/notify-decorator";
import { PaginatedResult } from "src/app/support/paginated-result";
import { environment } from "src/environments/environment";
import { sntTranslate } from "src/app/support/translate";

@Injectable()
export class CashClosureBackend {
  private readonly http = inject(HttpClient);
  private readonly templateService = inject(TemplateService);
  private readonly printService = inject(PrintService);
  private readonly storageBackend = inject(StorageBackend);
  private readonly paymentMethodBackend = inject(PaymentMethodBackend);
  private readonly letterheadBackend = inject(LetterheadBackend);
  private readonly regionService = inject(RegionService);

  search(request: {
    paidAtFrom?: string | undefined;
    paidAtTo?: string | undefined;
    fiscalProfileId?: string | undefined;
  }): Observable<PaginatedResult<CashClosure>> {
    return this.http.get<PaginatedResult<CashClosure>>(
      `${environment.snoots.api.billing}/payments`,
      {
        params: <any>pickBy({
          paidAtFrom: request.paidAtFrom,
          paidAtTo: request.paidAtTo,
          fiscalProfileId: request.fiscalProfileId
        })
      }
    );
  }

  @UserNotify({
    successMessage: marker("Chiusura di cassa effettuata"),
    errorMessage: marker("Impossibile effettuare la chiusura di cassa")
  })
  export(request: {
    paidAtFrom?: string | undefined;
    paidAtTo?: string | undefined;
    fiscalProfileId?: string | undefined;
  }): Observable<any> {
    return this.http.post<{ content: string; fileName: string }>(
      `${environment.snoots.api.billing}/payments/exports`,
      {
        filter: {
          ...request
        }
      }
    );
  }

  async print(request: {
    paidAtFrom?: string | undefined;
    paidAtTo?: string | undefined;
    fiscalProfileId?: string | undefined;
  }): Promise<void> {
    try {
      await this.printService.printPdf(
        this.toPdf(
          request.paidAtFrom,
          request.paidAtTo,
          request.fiscalProfileId
        )
      );
    } catch (ex) {
      console.error(ex);
      throw ex;
    }
  }

  async toPdf(
    paidAtFrom?: string | undefined,
    paidAtTo?: string | undefined,
    fiscalProfileId?: string | undefined
  ): Promise<Blob> {
    const [
      invoiceTemplate,
      tailwindCss,
      invoiceHeaderTemplate,
      invoiceFooterTemplate
    ] = await Promise.all([
      fetch("/assets/ejs-templates/cash-closure/cash-closure.ejs").then(
        (response) => response.text()
      ),
      fetch("/assets/ejs-templates/invoice/tailwind.css").then((response) =>
        response.text()
      ),
      fetch("/assets/ejs-templates/header.ejs").then((response) =>
        response.text()
      ),
      fetch("/assets/ejs-templates/invoice/invoice-footer.ejs").then(
        (response) => response.text()
      )
    ]);

    const [cashClosure, count, defaultLetterhead] = await Promise.all([
      lastValueFrom(
        this.search({
          paidAtFrom,
          paidAtTo,
          fiscalProfileId
        })
      ),
      lastValueFrom(
        this.count({
          paidAtFrom,
          paidAtTo,
          fiscalProfileId
        })
      ),
      lastValueFrom(this.letterheadBackend.loadDefaultLight())
    ]);

    const paymentMethods: { key: string; value: number }[] = [];

    await Promise.all(
      Object.keys(count.partials).map(async (key) => {
        const paymentMethod = await lastValueFrom(
          this.paymentMethodBackend.load(key)
        );
        const value = count.partials[key];
        paymentMethods.push({ key: paymentMethod?.name, value });
      })
    );

    const viewModel = paymentToPaymentPrintViewModel({
      cashClosure: cashClosure.docs.map((x) => x),
      total: count.total,
      paymentMethods,
      regionService: this.regionService
    });

    const { logoData, secondaryImageData } = await lastValueFrom(
      forkJoin({
        logoData: iif(
          () => !!defaultLetterhead?.header?.logo?.id,
          defer(() =>
            this.storageBackend
              .download(defaultLetterhead?.header!.logo!.id!)
              .pipe(
                switchMap((downloadUrl: string) =>
                  from(
                    fetch(downloadUrl)
                      .then((response) => response.blob())
                      .then((blob) => new Blob([blob], { type: "image/png" }))
                      .then((imageBlob) => blobToDataUrl(imageBlob))
                  )
                ),
                catchError(() => of(undefined))
              )
          ),
          of(undefined)
        ),
        secondaryImageData: iif(
          () => !!defaultLetterhead?.header?.secondaryImage?.id,
          defer(() =>
            this.storageBackend
              .download(defaultLetterhead?.header!.secondaryImage!.id!)
              .pipe(
                switchMap((downloadUrl: string) =>
                  from(
                    fetch(downloadUrl)
                      .then((response) => response.blob())
                      .then((blob) => new Blob([blob], { type: "image/png" }))
                      .then((imageBlob) => blobToDataUrl(imageBlob))
                  )
                ),
                catchError(() => of(undefined))
              )
          ),
          of(undefined)
        )
      })
    );

    const htmlHeadeRendered = this.templateService.render(
      invoiceHeaderTemplate,
      {
        ...viewModel,
        styles: "",
        content: defaultLetterhead?.header?.html || "",
        title: "",
        logo: logoData,
        secondaryImage: secondaryImageData,
        logoSize: defaultLetterhead?.header?.logo?.size || 1,
        secondaryImageSize:
          defaultLetterhead?.header?.secondaryImage?.size || 1,
        tailwindCss
      }
    );

    const htmlFooterRendered = this.templateService.render(
      invoiceFooterTemplate,
      {
        ...viewModel,
        content: undefined,
        tailwindCss
      }
    );

    const htmlRendered = this.templateService.render(invoiceTemplate, {
      ...viewModel,
      tailwindCss,
      dataDiIncassoLabel: sntTranslate(marker("Data di incasso")),
      contattoLabel: sntTranslate(marker("Contatto")),
      profiloFiscaleLabel: sntTranslate(marker("Profilo fiscale")),
      fatturaLabel: sntTranslate(marker("Fattura")),
      metodoDiPagamentoLabel: sntTranslate(marker("Metodo di pagamento")),
      totaleIncassatoLabel: sntTranslate(marker("Totale incassato")),
      totaleLabel: sntTranslate(marker("Totale"))
    });

    return await this.printService.htmlToPdf({
      header: htmlHeadeRendered,
      footer: htmlFooterRendered,
      content: htmlRendered
    });
  }

  count(request: {
    paidAtFrom?: string | undefined;
    paidAtTo?: string | undefined;
    fiscalProfileId?: string | undefined;
  }): Observable<{
    total: number;
    partials: { [key: string]: number };
  }> {
    return this.http.get<{
      total: number;
      partials: { [key: string]: number };
    }>(`${environment.snoots.api.billing}/payments/total`, {
      params: <any>pickBy({
        paidAtFrom: request.paidAtFrom,
        paidAtTo: request.paidAtTo,
        fiscalProfileId: request.fiscalProfileId
      })
    });
  }
}

function paymentToPaymentPrintViewModel({
  cashClosure,
  total,
  paymentMethods,
  regionService
}: {
  cashClosure: CashClosure[];
  total: number;
  paymentMethods: { key: string; value: number }[];
  regionService: RegionService;
}): CashClosurePrintViewModel {
  return {
    footerName: "",
    total: formatCurrency({ value: round(total, 2), regionService }),
    paymentMethods: paymentMethods.map((x) => ({
      name: x.key,
      amount: formatCurrency({ value: round(x.value, 2), regionService })
    })),
    rows: cashClosure.map((cash) => ({
      paidAt: dateUtil(cash.paidAt).format("DD/MM/YYYY"),
      customerNominative: getCustomerNominative(cash.customer),
      amount: formatCurrency({ value: round(cash.amount, 2), regionService }),
      paymentMethodName: cash.paymentMethod?.name || "",
      invoiceName: cash.invoice?.number || "",
      fiscalProfileName: cash.fiscalProfile.name || ""
    }))
  };
}
